import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors,
} from "@dnd-kit/core";
import {
  SortableContext, verticalListSortingStrategy, useSortable, arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  ArrowLeft, Save, Plus, Trash2, GripVertical,
  CheckCircle2, CheckSquare, ToggleLeft, AlignLeft, ArrowLeftRight,
  List, BookOpen, ImageIcon, Crosshair, Settings, Database,
  Loader2, X, Copy, Clock, ChevronDown, Info, RotateCcw,
} from "lucide-react";
import { api } from "../../api/client";
import { toast } from "sonner";

// ─── question type registry ─────────────────────────────────────────────────

const Q_TYPES = [
  { type: "multiple_choice", label: "Multiple Choice",  icon: CheckCircle2,    desc: "One correct answer"       },
  { type: "multiple_select", label: "Multiple Select",  icon: CheckSquare,     desc: "Many correct answers"     },
  { type: "true_false",      label: "True / False",     icon: ToggleLeft,      desc: "Binary choice"            },
  { type: "fill_blank",      label: "Fill in the Blank",icon: AlignLeft,       desc: "Typed text answer"        },
  { type: "matching",        label: "Matching",         icon: ArrowLeftRight,  desc: "Match pairs"              },
  { type: "ordering",        label: "Ordering",         icon: List,            desc: "Arrange in sequence"      },
  { type: "scenario",        label: "Scenario",         icon: BookOpen,        desc: "Scenario context + MCQ"   },
  { type: "image_select",    label: "Image Selection",  icon: ImageIcon,       desc: "Click the correct image"  },
  { type: "hotspot",         label: "Hotspot",          icon: Crosshair,       desc: "Click an area on image"   },
];

const Q_DEFAULTS = {
  multiple_choice: { text: "", options: ["", "", "", ""], correct_index: 0, explanation: "", answer_feedback: {} },
  multiple_select: { text: "", options: ["", "", "", ""], correct_indices: [], explanation: "", answer_feedback: {} },
  true_false:      { text: "", correct_index: 0, explanation: "" },
  fill_blank:      { text: "", correct_answer: "", case_sensitive: false, explanation: "" },
  matching:        { text: "", pairs: [{ left: "", right: "" }, { left: "", right: "" }], explanation: "" },
  ordering:        { text: "", items: ["", "", ""], explanation: "" },
  scenario:        { scenario_text: "", text: "", options: ["", "", "", ""], correct_index: 0, explanation: "", answer_feedback: {} },
  image_select:    { text: "", image_urls: ["", "", ""], correct_index: 0, explanation: "" },
  hotspot:         { text: "", hotspot_image: "", hotspot_x: 50, hotspot_y: 50, hotspot_radius: 10, explanation: "" },
};

const DEFAULT_SETTINGS = {
  pass_mark_percent:    60,
  randomise_questions:  false,
  randomise_answers:    false,
  time_limit_minutes:   null,
  retry_rule:           "unlimited",
  max_attempts:         3,
  lockout_minutes:      30,
};

const uid = () => `q_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 5)}`;
const mkQuestion = (type) => ({ id: uid(), type, time_limit_seconds: null, ...Q_DEFAULTS[type] });

// ─── sortable wrapper ───────────────────────────────────────────────────────

function Sortable({ id, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.4 : 1, position: "relative", zIndex: isDragging ? 50 : "auto" }}
      {...attributes}
    >
      {children(listeners)}
    </div>
  );
}

// ─── shared input style ─────────────────────────────────────────────────────

const inp = "w-full bg-[#0E0F12] border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#CFFF50]/40 text-white placeholder-gray-600";
const label = "block text-[10px] uppercase tracking-widest text-gray-500 font-semibold mb-1.5";

// ─── question type–specific editors ────────────────────────────────────────

function MCQEditor({ q, update, showFeedback = true }) {
  const options = q.options || ["", "", "", ""];
  const setOpt = (i, v) => update({ options: options.map((o, j) => j === i ? v : o) });
  const setFeedback = (i, v) => update({ answer_feedback: { ...q.answer_feedback, [i]: v } });
  const addOpt  = () => update({ options: [...options, ""] });
  const delOpt  = (i) => update({ options: options.filter((_, j) => j !== i), correct_index: q.correct_index >= i && q.correct_index > 0 ? q.correct_index - 1 : q.correct_index });

  return (
    <div className="space-y-2">
      {options.map((opt, i) => (
        <div key={i} className="flex items-center gap-2">
          <input
            type="radio" name={`correct_${q.id}`}
            checked={q.correct_index === i}
            onChange={() => update({ correct_index: i })}
            className="accent-[#CFFF50] shrink-0 w-4 h-4"
          />
          <input className={`${inp} flex-1`} placeholder={`Option ${i + 1}`} value={opt} onChange={(e) => setOpt(i, e.target.value)} />
          {showFeedback && (
            <input className="w-40 bg-[#0E0F12] border border-white/10 rounded-lg px-2 py-2 text-xs focus:outline-none focus:border-white/20 text-gray-400 placeholder-gray-700"
              placeholder="Feedback…" value={(q.answer_feedback || {})[i] || ""} onChange={(e) => setFeedback(i, e.target.value)}
            />
          )}
          {options.length > 2 && (
            <button onClick={() => delOpt(i)} className="text-gray-700 hover:text-red-400 shrink-0"><X size={13} /></button>
          )}
        </div>
      ))}
      <button onClick={addOpt} className="text-xs text-[#CFFF50]/70 hover:text-[#CFFF50] flex items-center gap-1">
        <Plus size={11} /> Add option
      </button>
    </div>
  );
}

function MultiSelectEditor({ q, update }) {
  const options = q.options || ["", "", "", ""];
  const correct = q.correct_indices || [];
  const setOpt  = (i, v) => update({ options: options.map((o, j) => j === i ? v : o) });
  const toggleCorrect = (i) => {
    const next = correct.includes(i) ? correct.filter((x) => x !== i) : [...correct, i];
    update({ correct_indices: next });
  };
  const addOpt = () => update({ options: [...options, ""] });
  const delOpt = (i) => update({ options: options.filter((_, j) => j !== i), correct_indices: correct.filter((x) => x !== i).map((x) => x > i ? x - 1 : x) });
  const setFeedback = (i, v) => update({ answer_feedback: { ...q.answer_feedback, [i]: v } });

  return (
    <div className="space-y-2">
      {options.map((opt, i) => (
        <div key={i} className="flex items-center gap-2">
          <input type="checkbox" checked={correct.includes(i)} onChange={() => toggleCorrect(i)} className="accent-[#CFFF50] shrink-0 w-4 h-4" />
          <input className={`${inp} flex-1`} placeholder={`Option ${i + 1}`} value={opt} onChange={(e) => setOpt(i, e.target.value)} />
          <input className="w-40 bg-[#0E0F12] border border-white/10 rounded-lg px-2 py-2 text-xs focus:outline-none text-gray-400 placeholder-gray-700"
            placeholder="Feedback…" value={(q.answer_feedback || {})[i] || ""} onChange={(e) => setFeedback(i, e.target.value)} />
          {options.length > 2 && <button onClick={() => delOpt(i)} className="text-gray-700 hover:text-red-400 shrink-0"><X size={13} /></button>}
        </div>
      ))}
      <button onClick={addOpt} className="text-xs text-[#CFFF50]/70 hover:text-[#CFFF50] flex items-center gap-1"><Plus size={11} /> Add option</button>
    </div>
  );
}

function TrueFalseEditor({ q, update }) {
  return (
    <div className="flex gap-3 pt-1">
      {["True", "False"].map((label, i) => (
        <button
          key={label}
          onClick={() => update({ correct_index: i })}
          className={`flex-1 py-3 rounded-xl font-bold text-sm border-2 transition-all ${
            q.correct_index === i
              ? i === 0 ? "border-[#CFFF50] bg-[#CFFF50]/10 text-[#CFFF50]" : "border-red-500 bg-red-500/10 text-red-400"
              : "border-white/10 text-gray-500 hover:border-white/20"
          }`}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

function FillBlankEditor({ q, update }) {
  return (
    <div className="space-y-3 pt-1">
      <div>
        <label className={label}>Correct answer (exact match)</label>
        <input className={inp} placeholder="Expected answer" value={q.correct_answer || ""} onChange={(e) => update({ correct_answer: e.target.value })} />
      </div>
      <label className="flex items-center gap-2 cursor-pointer">
        <input type="checkbox" checked={!!q.case_sensitive} onChange={(e) => update({ case_sensitive: e.target.checked })} className="accent-[#CFFF50]" />
        <span className="text-xs text-gray-400">Case sensitive</span>
      </label>
    </div>
  );
}

function MatchingEditor({ q, update }) {
  const pairs = q.pairs || [];
  const setPair = (i, side, v) => update({ pairs: pairs.map((p, j) => j === i ? { ...p, [side]: v } : p) });
  const addPair = () => update({ pairs: [...pairs, { left: "", right: "" }] });
  const delPair = (i) => update({ pairs: pairs.filter((_, j) => j !== i) });

  return (
    <div className="space-y-2 pt-1">
      <div className="grid grid-cols-2 gap-2 text-[10px] text-gray-600 uppercase tracking-widest px-1 mb-1">
        <span>Left item</span><span>Right item (match)</span>
      </div>
      {pairs.map((pair, i) => (
        <div key={i} className="flex items-center gap-2">
          <input className={`${inp} flex-1`} placeholder="Left…"  value={pair.left}  onChange={(e) => setPair(i, "left",  e.target.value)} />
          <ArrowLeftRight size={13} className="text-gray-600 shrink-0" />
          <input className={`${inp} flex-1`} placeholder="Right…" value={pair.right} onChange={(e) => setPair(i, "right", e.target.value)} />
          {pairs.length > 2 && <button onClick={() => delPair(i)} className="text-gray-700 hover:text-red-400 shrink-0"><X size={13} /></button>}
        </div>
      ))}
      <button onClick={addPair} className="text-xs text-[#CFFF50]/70 hover:text-[#CFFF50] flex items-center gap-1"><Plus size={11} /> Add pair</button>
    </div>
  );
}

function OrderingEditor({ q, update }) {
  const items   = q.items || [];
  const setItem = (i, v) => update({ items: items.map((x, j) => j === i ? v : x) });
  const addItem = () => update({ items: [...items, ""] });
  const delItem = (i) => update({ items: items.filter((_, j) => j !== i) });
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));
  const ids     = items.map((_, i) => `ord_${i}`);

  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const oi = ids.indexOf(active.id);
    const ni = ids.indexOf(over.id);
    update({ items: arrayMove(items, oi, ni) });
  };

  return (
    <div className="space-y-2 pt-1">
      <p className="text-xs text-gray-600">Drag items into the correct order — this is what learners must replicate.</p>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={ids} strategy={verticalListSortingStrategy}>
          {items.map((item, i) => (
            <Sortable key={ids[i]} id={ids[i]}>
              {(drag) => (
                <div className="flex items-center gap-2">
                  <span {...drag} className="text-gray-600 hover:text-gray-400 cursor-grab active:cursor-grabbing touch-none"><GripVertical size={14} /></span>
                  <span className="w-5 h-5 rounded-full bg-[#7C5CFF]/20 text-[#7C5CFF] text-[10px] font-bold flex items-center justify-center shrink-0">{i + 1}</span>
                  <input className={`${inp} flex-1`} placeholder={`Item ${i + 1}`} value={item} onChange={(e) => setItem(i, e.target.value)} />
                  {items.length > 2 && <button onClick={() => delItem(i)} className="text-gray-700 hover:text-red-400 shrink-0"><X size={13} /></button>}
                </div>
              )}
            </Sortable>
          ))}
        </SortableContext>
      </DndContext>
      <button onClick={addItem} className="text-xs text-[#CFFF50]/70 hover:text-[#CFFF50] flex items-center gap-1"><Plus size={11} /> Add item</button>
    </div>
  );
}

function ImageSelectEditor({ q, update }) {
  const urls = q.image_urls || ["", "", ""];
  const setUrl = (i, v) => update({ image_urls: urls.map((u, j) => j === i ? v : u) });
  const addUrl = () => update({ image_urls: [...urls, ""] });
  const delUrl = (i) => update({ image_urls: urls.filter((_, j) => j !== i), correct_index: q.correct_index >= i && q.correct_index > 0 ? q.correct_index - 1 : q.correct_index });

  return (
    <div className="space-y-3 pt-1">
      <p className="text-xs text-gray-500">Select the radio button on the correct image.</p>
      <div className="grid grid-cols-2 gap-3">
        {urls.map((url, i) => (
          <div key={i} className={`rounded-xl border-2 overflow-hidden transition-all ${q.correct_index === i ? "border-[#CFFF50]" : "border-white/10"}`}>
            <div className="aspect-video bg-black relative">
              {url
                ? <img src={url} alt={`Option ${i + 1}`} className="w-full h-full object-cover" />
                : <div className="w-full h-full flex items-center justify-center text-gray-700"><ImageIcon size={20} /></div>
              }
            </div>
            <div className="flex items-center gap-2 px-2 py-1.5 bg-[#0E0F12]">
              <input type="radio" name={`img_correct_${q.id}`} checked={q.correct_index === i} onChange={() => update({ correct_index: i })} className="accent-[#CFFF50] shrink-0" />
              <input className="flex-1 bg-transparent text-xs text-gray-400 focus:outline-none placeholder-gray-700" placeholder="Image URL…" value={url} onChange={(e) => setUrl(i, e.target.value)} />
              {urls.length > 2 && <button onClick={() => delUrl(i)} className="text-gray-700 hover:text-red-400"><X size={11} /></button>}
            </div>
          </div>
        ))}
      </div>
      <button onClick={addUrl} className="text-xs text-[#CFFF50]/70 hover:text-[#CFFF50] flex items-center gap-1"><Plus size={11} /> Add image</button>
    </div>
  );
}

function HotspotEditor({ q, update }) {
  const imgRef = useRef(null);
  const handleClick = (e) => {
    if (!imgRef.current) return;
    const rect = imgRef.current.getBoundingClientRect();
    const x = Math.round(((e.clientX - rect.left) / rect.width) * 100);
    const y = Math.round(((e.clientY - rect.top)  / rect.height) * 100);
    update({ hotspot_x: x, hotspot_y: y });
  };

  return (
    <div className="space-y-3 pt-1">
      <div>
        <label className={label}>Background image URL</label>
        <input className={inp} placeholder="https://…" value={q.hotspot_image || ""} onChange={(e) => update({ hotspot_image: e.target.value })} />
      </div>
      {q.hotspot_image && (
        <div className="rounded-xl overflow-hidden border border-white/10 relative cursor-crosshair" style={{ aspectRatio: "16/9" }}>
          <img ref={imgRef} src={q.hotspot_image} alt="hotspot" className="w-full h-full object-cover select-none" onClick={handleClick} draggable={false} />
          <div
            className="absolute border-2 border-[#CFFF50] rounded-full bg-[#CFFF50]/20 pointer-events-none"
            style={{
              left: `${q.hotspot_x}%`, top: `${q.hotspot_y}%`,
              width: `${q.hotspot_radius * 2}%`, height: `${q.hotspot_radius * 2}%`,
              transform: "translate(-50%, -50%)",
            }}
          />
          <p className="absolute bottom-2 left-2 text-[10px] bg-black/60 text-white px-2 py-1 rounded">
            Click image to reposition hotspot
          </p>
        </div>
      )}
      <div className="grid grid-cols-3 gap-2">
        {[["X %", "hotspot_x"], ["Y %", "hotspot_y"], ["Radius %", "hotspot_radius"]].map(([l, k]) => (
          <div key={k}>
            <label className={label}>{l}</label>
            <input type="number" min={1} max={100} className={inp} value={q[k] ?? 50} onChange={(e) => update({ [k]: parseFloat(e.target.value) || 50 })} />
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── question editor dispatcher ─────────────────────────────────────────────

function QuestionEditor({ q, update }) {
  if (!q) return null;
  const def  = Q_TYPES.find((t) => t.type === q.type) || Q_TYPES[0];
  const Icon = def.icon;

  return (
    <div className="flex-1 overflow-y-auto px-7 py-5">
      {/* Type selector */}
      <div className="flex items-center gap-2 mb-5">
        <Icon size={15} className="text-[#CFFF50] shrink-0" />
        <span className="text-xs font-bold text-[#CFFF50] uppercase tracking-widest">{def.label}</span>
        <TypeSelector currentType={q.type} onSelect={(type) => update({ ...Q_DEFAULTS[type], id: q.id, type, time_limit_seconds: q.time_limit_seconds })} />
      </div>

      {/* Scenario intro */}
      {q.type === "scenario" && (
        <div className="mb-4">
          <label className={label}>Scenario / Context</label>
          <textarea rows={3} className={`${inp} resize-y`} placeholder="Set the scene for the learner…" value={q.scenario_text || ""} onChange={(e) => update({ scenario_text: e.target.value })} />
        </div>
      )}

      {/* Question text */}
      <div className="mb-4">
        <label className={label}>Question</label>
        <textarea rows={q.type === "scenario" ? 2 : 3} className={`${inp} resize-y`} placeholder="Enter the question…" value={q.text || ""} onChange={(e) => update({ text: e.target.value })} />
      </div>

      {/* Type-specific editor */}
      <div className="mb-4">
        {q.type === "multiple_choice" && <MCQEditor q={q} update={update} />}
        {q.type === "multiple_select" && <MultiSelectEditor q={q} update={update} />}
        {q.type === "true_false"      && <TrueFalseEditor q={q} update={update} />}
        {q.type === "fill_blank"      && <FillBlankEditor q={q} update={update} />}
        {q.type === "matching"        && <MatchingEditor  q={q} update={update} />}
        {q.type === "ordering"        && <OrderingEditor  q={q} update={update} />}
        {q.type === "scenario"        && <MCQEditor q={q} update={update} />}
        {q.type === "image_select"    && <ImageSelectEditor q={q} update={update} />}
        {q.type === "hotspot"         && <HotspotEditor q={q} update={update} />}
      </div>

      {/* Explanation */}
      <div className="mb-4 pt-3 border-t border-white/[0.06]">
        <label className={label}>Explanation (shown after submission)</label>
        <textarea rows={2} className={`${inp} resize-y`} placeholder="Explain the correct answer…" value={q.explanation || ""} onChange={(e) => update({ explanation: e.target.value })} />
      </div>

      {/* Per-question time limit */}
      <div className="flex items-center gap-3">
        <label className="text-xs text-gray-500 whitespace-nowrap">Time limit</label>
        <input
          type="number" min={0} placeholder="None"
          className="w-24 bg-[#0E0F12] border border-white/10 rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:border-[#CFFF50]/40 text-center"
          value={q.time_limit_seconds ?? ""}
          onChange={(e) => update({ time_limit_seconds: e.target.value ? parseInt(e.target.value) : null })}
        />
        <span className="text-xs text-gray-500">seconds (0 = no limit)</span>
      </div>
    </div>
  );
}

function TypeSelector({ currentType, onSelect }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative ml-auto">
      <button onClick={() => setOpen((v) => !v)} className="flex items-center gap-1 text-xs text-gray-500 hover:text-white border border-white/10 hover:border-white/20 rounded-full px-3 py-1 transition-colors">
        Change type <ChevronDown size={11} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-1 z-20 bg-[#1a1c23] border border-white/10 rounded-2xl p-2 w-64 shadow-2xl">
            {Q_TYPES.map((t) => {
              const Icon = t.icon;
              return (
                <button key={t.type} onClick={() => { onSelect(t.type); setOpen(false); }}
                  className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-left transition-colors ${t.type === currentType ? "bg-[#CFFF50]/10 text-[#CFFF50]" : "hover:bg-white/[0.06] text-gray-300"}`}>
                  <Icon size={13} />
                  <div>
                    <p className="text-xs font-semibold">{t.label}</p>
                    <p className="text-[10px] text-gray-500">{t.desc}</p>
                  </div>
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

// ─── settings modal ─────────────────────────────────────────────────────────

function SettingsModal({ settings, onChange, onClose, description, onDescriptionChange }) {
  const set = (patch) => onChange({ ...settings, ...patch });
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <div className="w-full max-w-md bg-[#111318] border border-white/10 rounded-2xl overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/[0.08]">
          <h3 className="font-bold text-sm">Quiz Settings</h3>
          <button onClick={onClose} className="text-gray-600 hover:text-white"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-5 max-h-[70vh] overflow-y-auto">
          {/* Intro text */}
          <div>
            <label className={label}>Intro text <span className="normal-case text-gray-600 font-normal">(shown to students before they start)</span></label>
            <textarea
              rows={4}
              className={`${inp} resize-y`}
              placeholder="e.g. Questions are randomised and won't follow the same order as the course. Read each question carefully. You need 80% to pass."
              value={description}
              onChange={(e) => { onDescriptionChange(e.target.value); }}
            />
          </div>

          {/* Pass mark */}
          <div>
            <label className={label}>Pass mark (%)</label>
            <input type="number" min={1} max={100} className={inp}
              value={settings.pass_mark_percent} onChange={(e) => set({ pass_mark_percent: parseInt(e.target.value) || 60 })} />
          </div>

          {/* Randomise */}
          <div className="space-y-2">
            <label className={label}>Randomise</label>
            {[["randomise_questions", "Shuffle question order per attempt"], ["randomise_answers", "Shuffle answer order per question"]].map(([k, l]) => (
              <label key={k} className="flex items-center gap-2.5 cursor-pointer">
                <input type="checkbox" checked={!!settings[k]} onChange={(e) => set({ [k]: e.target.checked })} className="accent-[#CFFF50] w-4 h-4" />
                <span className="text-sm text-gray-300">{l}</span>
              </label>
            ))}
          </div>

          {/* Quiz time limit */}
          <div>
            <label className={label}>Quiz time limit (minutes, 0 = none)</label>
            <input type="number" min={0} className={inp}
              value={settings.time_limit_minutes ?? ""}
              placeholder="No limit"
              onChange={(e) => set({ time_limit_minutes: e.target.value ? parseInt(e.target.value) : null })} />
          </div>

          {/* Retry rule */}
          <div>
            <label className={label}>Retry rule</label>
            <div className="flex gap-2">
              {[["unlimited", "Unlimited"], ["n_attempts", "N Attempts"], ["timed_lockout", "Timed Lockout"]].map(([v, l]) => (
                <button key={v} onClick={() => set({ retry_rule: v })}
                  className={`flex-1 py-2 rounded-xl text-xs font-semibold border transition-all ${settings.retry_rule === v ? "border-[#CFFF50] bg-[#CFFF50]/10 text-[#CFFF50]" : "border-white/10 text-gray-500 hover:border-white/20"}`}>
                  {l}
                </button>
              ))}
            </div>
          </div>
          {settings.retry_rule === "n_attempts" && (
            <div>
              <label className={label}>Max attempts</label>
              <input type="number" min={1} className={inp} value={settings.max_attempts || 3} onChange={(e) => set({ max_attempts: parseInt(e.target.value) || 3 })} />
            </div>
          )}
          {settings.retry_rule === "timed_lockout" && (
            <div>
              <label className={label}>Lockout (minutes)</label>
              <input type="number" min={1} className={inp} value={settings.lockout_minutes || 30} onChange={(e) => set({ lockout_minutes: parseInt(e.target.value) || 30 })} />
            </div>
          )}
        </div>
        <div className="px-5 pb-5">
          <button onClick={onClose} className="w-full py-2.5 rounded-xl bg-[#CFFF50] text-[#0E0F12] font-bold text-sm hover:bg-[#d8ff6a] transition-colors">
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── question bank modal ────────────────────────────────────────────────────

function BankModal({ onAdd, onClose }) {
  const [bank, setBank]       = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/question-bank")
      .then(setBank)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const remove = async (id) => {
    try {
      await api.del(`/admin/question-bank/${id}`);
      setBank((prev) => prev.filter((q) => q.id !== id));
      toast.success("Removed from bank");
    } catch { toast.error("Failed to remove"); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <div className="w-full max-w-xl bg-[#111318] border border-white/10 rounded-2xl overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/[0.08]">
          <h3 className="font-bold text-sm flex items-center gap-2"><Database size={14} className="text-[#CFFF50]" /> Question Bank</h3>
          <button onClick={onClose} className="text-gray-600 hover:text-white"><X size={16} /></button>
        </div>
        <div className="max-h-[60vh] overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center py-12 text-gray-500"><Loader2 className="animate-spin mr-2" size={16} /> Loading…</div>
          ) : bank.length === 0 ? (
            <div className="text-center py-12">
              <Database size={28} className="text-gray-700 mx-auto mb-2" />
              <p className="text-sm text-gray-500">No questions saved yet.</p>
              <p className="text-xs text-gray-600 mt-1">Use "Save to Bank" on a question to add it here.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {bank.map((q) => {
                const def  = Q_TYPES.find((t) => t.type === q.type) || Q_TYPES[0];
                const Icon = def.icon;
                return (
                  <div key={q.id} className="flex items-start gap-3 bg-white/[0.03] border border-white/[0.08] rounded-xl p-3">
                    <Icon size={14} className="text-gray-500 shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-gray-300 line-clamp-2">{q.text || "Untitled question"}</p>
                      <p className="text-[10px] text-gray-600 mt-0.5">{def.label}</p>
                    </div>
                    <div className="flex gap-1.5 shrink-0">
                      <button onClick={() => { onAdd(q); onClose(); }}
                        className="px-3 py-1 rounded-lg bg-[#CFFF50] text-[#0E0F12] text-xs font-bold hover:bg-[#d8ff6a] transition-colors">
                        Add
                      </button>
                      <button onClick={() => remove(q.id)} className="p-1.5 rounded-lg text-gray-600 hover:text-red-400 hover:bg-red-500/10 transition-colors">
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── main studio ────────────────────────────────────────────────────────────

export default function QuizStudio() {
  const { courseId, lessonId } = useParams();
  const navigate                = useNavigate();

  // If lessonId is in the URL, operate on the lesson-level quiz
  const quizUrl = lessonId
    ? `/admin/courses/${courseId}/lessons/${lessonId}/quiz`
    : `/admin/courses/${courseId}/quiz`;
  const backUrl = lessonId
    ? `/admin/studio/courses/${courseId}`
    : "/admin/courses";

  const [quizTitle,       setQuizTitle]       = useState("");
  const [quizDescription, setQuizDescription] = useState("");
  const [courseTitle,     setCourseTitle]     = useState("");
  const [lessonTitle,     setLessonTitle]     = useState("");
  const [questions,     setQuestions]     = useState([]);
  const [settings,      setSettings]      = useState(DEFAULT_SETTINGS);
  const [selectedId,    setSelectedId]    = useState(null);
  const [saving,        setSaving]        = useState(false);
  const [dirty,         setDirty]         = useState(false);
  const [loading,       setLoading]       = useState(true);
  const [showSettings,  setShowSettings]  = useState(false);
  const [showBank,      setShowBank]      = useState(false);
  const [showTypePicker,setShowTypePicker]= useState(false);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  // Load
  useEffect(() => {
    Promise.all([
      api.get(quizUrl),
      api.get(`/admin/courses/${courseId}`),
    ]).then(([quiz, course]) => {
      setQuizTitle(quiz.title || "");
      setQuizDescription(quiz.description || "");
      setQuestions((quiz.questions || []).map((q) => ({ type: "multiple_choice", ...q })));
      setSettings({ ...DEFAULT_SETTINGS, ...(quiz.settings || {}) });
      setCourseTitle(course.title || "");
      if (lessonId) {
        const lesson = (course.curriculum || []).flatMap(s => s.lessons || []).find(l => l.id === lessonId);
        setLessonTitle(lesson?.title || lessonId);
      }
    }).catch(() => toast.error("Failed to load quiz"))
      .finally(() => setLoading(false));
  }, [courseId, lessonId]);

  const touch = () => setDirty(true);

  const selectedQ = questions.find((q) => q.id === selectedId) ?? null;

  // ── question ops ────────────────────────────────────────────────────────
  const addQuestion = (type) => {
    const q = mkQuestion(type);
    setQuestions((prev) => [...prev, q]);
    setSelectedId(q.id);
    setShowTypePicker(false);
    touch();
  };

  const addFromBank = (bankQ) => {
    const q = { ...bankQ, id: uid() };
    delete q.bank_id;
    delete q.created_at;
    setQuestions((prev) => [...prev, q]);
    setSelectedId(q.id);
    touch();
  };

  const deleteQuestion = (id) => {
    setQuestions((prev) => prev.filter((q) => q.id !== id));
    if (selectedId === id) setSelectedId(null);
    touch();
  };

  const duplicateQuestion = (q) => {
    const copy = { ...q, id: uid() };
    setQuestions((prev) => {
      const i = prev.findIndex((x) => x.id === q.id);
      const next = [...prev];
      next.splice(i + 1, 0, copy);
      return next;
    });
    setSelectedId(copy.id);
    touch();
  };

  const updateQuestion = (id, patch) => {
    setQuestions((prev) => prev.map((q) => q.id === id ? { ...q, ...patch } : q));
    touch();
  };

  const saveToBank = async (q) => {
    try {
      const { id, ...rest } = q;
      await api.post("/admin/question-bank", rest);
      toast.success("Saved to Question Bank");
    } catch { toast.error("Failed to save to bank"); }
  };

  // ── dnd ─────────────────────────────────────────────────────────────────
  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    setQuestions((prev) => {
      const oi = prev.findIndex((q) => q.id === active.id);
      const ni = prev.findIndex((q) => q.id === over.id);
      return arrayMove(prev, oi, ni);
    });
    touch();
  };

  // ── save ─────────────────────────────────────────────────────────────────
  const save = async () => {
    setSaving(true);
    try {
      await api.put(quizUrl, { title: quizTitle, description: quizDescription, questions, settings });
      setDirty(false);
      toast.success("Quiz saved");
    } catch { toast.error("Save failed"); }
    finally  { setSaving(false); }
  };

  if (loading) {
    return (
      <div className="h-screen bg-[#0E0F12] flex items-center justify-center text-gray-500">
        <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading quiz studio…
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-[#0E0F12] text-white overflow-hidden select-none">

      {/* ── Top bar ─────────────────────────────────────────────────────── */}
      <header className="h-14 shrink-0 flex items-center gap-3 px-4 border-b border-white/[0.08] bg-[#09090c]">
        <button onClick={() => navigate(backUrl)}
          className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-white transition-colors pr-3 border-r border-white/[0.08]">
          <ArrowLeft size={14} /> {lessonId ? "Content" : "Courses"}
        </button>

        <div className="flex-1 flex flex-col min-w-0">
          <span className="text-[10px] text-gray-600 truncate">
            {courseTitle}{lessonTitle ? ` · ${lessonTitle}` : ""}
          </span>
          <input
            value={quizTitle}
            onChange={(e) => { setQuizTitle(e.target.value); touch(); }}
            className="bg-transparent text-sm font-semibold text-white focus:outline-none min-w-0 leading-tight"
            placeholder="Quiz title"
          />
        </div>

        {dirty && <span className="text-[10px] text-gray-600 shrink-0 hidden sm:block">Unsaved</span>}

        <div className="flex items-center gap-2 shrink-0">
          <span className="text-xs text-gray-600 hidden md:block">{questions.length} question{questions.length !== 1 ? "s" : ""}</span>

          <button onClick={() => setShowBank(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-white/10 text-xs text-gray-400 hover:text-white hover:border-white/20 transition-colors">
            <Database size={12} /> Bank
          </button>

          <button onClick={() => setShowSettings(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-white/10 text-xs text-gray-400 hover:text-white hover:border-white/20 transition-colors">
            <Settings size={12} /> Settings
          </button>

          <button onClick={save} disabled={saving || !dirty}
            className="flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-[#CFFF50] text-[#0E0F12] text-sm font-bold hover:bg-[#d8ff6a] transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
            {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Save
          </button>
        </div>
      </header>

      {/* ── Main split ──────────────────────────────────────────────────── */}
      <div className="flex-1 flex overflow-hidden">

        {/* ── Left — question list ─────────────────────────────────────── */}
        <aside className="w-72 shrink-0 flex flex-col border-r border-white/[0.08] bg-[#07080a] overflow-hidden">
          <div className="px-4 py-2.5 border-b border-white/[0.06]">
            <span className="text-[10px] font-bold uppercase tracking-widest text-gray-600">Questions</span>
          </div>

          <div className="flex-1 overflow-y-auto py-2">
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={questions.map((q) => q.id)} strategy={verticalListSortingStrategy}>
                {questions.map((q, i) => {
                  const def     = Q_TYPES.find((t) => t.type === q.type) || Q_TYPES[0];
                  const Icon    = def.icon;
                  const isSelected = q.id === selectedId;
                  return (
                    <Sortable key={q.id} id={q.id}>
                      {(drag) => (
                        <div
                          onClick={() => setSelectedId(q.id)}
                          className={`group flex items-center gap-2 mx-2 px-2 py-2 rounded-lg cursor-pointer mb-0.5 transition-colors border ${
                            isSelected ? "bg-[#CFFF50]/10 border-[#CFFF50]/20" : "hover:bg-white/[0.04] border-transparent"
                          }`}
                        >
                          <span {...drag} className="text-gray-700 hover:text-gray-400 cursor-grab active:cursor-grabbing shrink-0 touch-none" onClick={(e) => e.stopPropagation()}>
                            <GripVertical size={13} />
                          </span>
                          <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-black shrink-0 ${isSelected ? "bg-[#CFFF50] text-[#0E0F12]" : "bg-white/10 text-gray-400"}`}>
                            {i + 1}
                          </span>
                          <Icon size={11} className={isSelected ? "text-[#CFFF50] shrink-0" : "text-gray-600 shrink-0"} />
                          <span className={`flex-1 text-xs truncate ${isSelected ? "text-[#CFFF50] font-semibold" : "text-gray-400"}`}>
                            {q.text || <span className="text-gray-700 italic">Untitled</span>}
                          </span>
                          <div className="opacity-0 group-hover:opacity-100 flex gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
                            <button onClick={() => duplicateQuestion(q)} className="text-gray-700 hover:text-gray-300 transition-colors"><Copy size={10} /></button>
                            <button onClick={() => deleteQuestion(q.id)} className="text-gray-700 hover:text-red-400 transition-colors"><X size={10} /></button>
                          </div>
                        </div>
                      )}
                    </Sortable>
                  );
                })}
              </SortableContext>
            </DndContext>

            {questions.length === 0 && (
              <div className="px-4 py-10 text-center">
                <p className="text-xs text-gray-600">No questions yet.</p>
                <p className="text-xs text-gray-700 mt-0.5">Add a question below.</p>
              </div>
            )}
          </div>

          {/* Add question */}
          <div className="p-3 border-t border-white/[0.06] space-y-1.5">
            <div className="relative">
              <button onClick={() => setShowTypePicker((v) => !v)}
                className="w-full flex items-center justify-center gap-1.5 py-2 rounded-xl bg-[#CFFF50]/10 border border-[#CFFF50]/20 text-xs font-semibold text-[#CFFF50] hover:bg-[#CFFF50]/15 transition-colors">
                <Plus size={12} /> Add Question
              </button>
              {showTypePicker && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setShowTypePicker(false)} />
                  <div className="absolute bottom-full mb-2 left-0 right-0 z-20 bg-[#1a1c23] border border-white/10 rounded-2xl p-2 shadow-2xl">
                    {Q_TYPES.map((t) => {
                      const Icon = t.icon;
                      return (
                        <button key={t.type} onClick={() => addQuestion(t.type)}
                          className="w-full flex items-center gap-2.5 px-3 py-2 rounded-xl hover:bg-white/[0.06] transition-colors text-left">
                          <Icon size={13} className="text-gray-500 shrink-0" />
                          <div>
                            <p className="text-xs font-semibold text-gray-300">{t.label}</p>
                            <p className="text-[10px] text-gray-600">{t.desc}</p>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
            <button onClick={() => setShowBank(true)}
              className="w-full flex items-center justify-center gap-1.5 py-2 rounded-xl border border-dashed border-white/15 text-xs text-gray-500 hover:text-white hover:border-white/30 transition-colors">
              <Database size={11} /> From Question Bank
            </button>
          </div>
        </aside>

        {/* ── Right — question editor ──────────────────────────────────── */}
        <main className="flex-1 flex flex-col overflow-hidden">
          {!selectedQ ? (
            <div className="flex-1 flex flex-col items-center justify-center text-center p-16">
              <div className="w-16 h-16 rounded-2xl bg-white/[0.03] border border-white/[0.06] flex items-center justify-center mb-4">
                <CheckCircle2 size={22} className="text-gray-700" />
              </div>
              <p className="text-gray-400 font-semibold">Select a question to edit</p>
              <p className="text-sm text-gray-600 mt-1">
                {questions.length === 0
                  ? "Add your first question from the panel on the left."
                  : "Click any question in the list to open its editor."}
              </p>
            </div>
          ) : (
            <div className="flex-1 flex flex-col overflow-hidden">
              {/* Question header */}
              <div className="px-7 py-3.5 border-b border-white/[0.08] shrink-0 bg-[#0a0b0e] flex items-center gap-3">
                <span className="text-xs text-gray-500">Q{questions.findIndex((q) => q.id === selectedId) + 1} of {questions.length}</span>
                <div className="flex-1" />
                <button onClick={() => saveToBank(selectedQ)}
                  className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-white border border-white/10 hover:border-white/20 rounded-full px-3 py-1 transition-colors">
                  <Database size={11} /> Save to Bank
                </button>
                <button onClick={() => duplicateQuestion(selectedQ)}
                  className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-white border border-white/10 hover:border-white/20 rounded-full px-3 py-1 transition-colors">
                  <Copy size={11} /> Duplicate
                </button>
                <button onClick={() => deleteQuestion(selectedId)}
                  className="flex items-center gap-1.5 text-xs text-red-400/60 hover:text-red-400 border border-red-500/10 hover:border-red-500/30 rounded-full px-3 py-1 transition-colors">
                  <Trash2 size={11} /> Delete
                </button>
              </div>
              <QuestionEditor
                q={selectedQ}
                update={(patch) => updateQuestion(selectedId, patch)}
              />
            </div>
          )}
        </main>
      </div>

      {/* ── Modals ──────────────────────────────────────────────────────── */}
      {showSettings && <SettingsModal settings={settings} onChange={setSettings} onClose={() => { setShowSettings(false); touch(); }} description={quizDescription} onDescriptionChange={(v) => { setQuizDescription(v); touch(); }} />}
      {showBank     && <BankModal onAdd={addFromBank} onClose={() => setShowBank(false)} />}
    </div>
  );
}
